import type { Dish } from '@/types/dish';
import { baseDish, t } from './mockCommon';

/**
 * 1. Cassoulet (en tant qu'Entrée - ID -6)
 * Prix corrigés pour représenter le COÛT TOTAL de la ligne (et non le prix au gramme)
 */
function createCassouletDishStarter(): Dish {
  return {
    ...baseDish,
    id: -6,
    name: t('onboarding.mockData.dishes.cassoulet.name'),
    difficulty: 3,
    prep_time_min: 50,
    cook_time_min: 150,
    chill_time_min: 0,
    servings: 10,
    selected_image: 'none',
    allergens: `${t('onboarding.mockData.dishes.cassoulet.allergens.dairy')}, ${t('onboarding.mockData.dishes.cassoulet.allergens.celery')}`,
    calories_kcal: 880,
    protein_g: 44.0,
    fat_g: 48.0,
    saturated_fat_g: 14.0,
    carbohydrates_g: 66.0,
    sugars_g: 8.0,
    dietary_fiber_g: 14.0,
    salt_sodium_g: 2.5,
    ingredients: [
      // 250g * ~30.80 CHF/kg = 7.70
      { id: 101, category: 'meat', name: t('onboarding.mockData.dishes.cassoulet.ingredients.duckLegs'), quantity: 250, unit: 'g', price: 7.7 },
      { id: 102, category: 'meat', name: t('onboarding.mockData.dishes.cassoulet.ingredients.sausages'), quantity: 100, unit: 'g', price: 1.75 },
      {
        id: 103,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.squash'),
        quantity: 200,
        unit: 'g',
        price: 1.2,
      },
      { id: 104, category: 'fruits_veggies', name: t('onboarding.mockData.dishes.cassoulet.ingredients.onion'), quantity: 50, unit: 'g', price: 0.2 },
      {
        id: 105,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.garlic'),
        quantity: 5,
        unit: 'g',
        price: 0.05,
      },
      {
        id: 106,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.carrot'),
        quantity: 30,
        unit: 'g',
        price: 0.15,
      },
      {
        id: 107,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.celery'),
        quantity: 20,
        unit: 'g',
        price: 0.1,
      },
      {
        id: 108,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.beans'),
        quantity: 100,
        unit: 'g',
        price: 0.6,
      },
      {
        id: 109,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.breadcrumbs'),
        quantity: 10,
        unit: 'g',
        price: 0.1,
      },
      {
        id: 110,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.chickenBroth'),
        quantity: 200,
        unit: 'ml',
        price: 0.4,
      },
      { id: 111, category: 'frozen', name: t('onboarding.mockData.dishes.cassoulet.ingredients.tomatoes'), quantity: 40, unit: 'g', price: 0.2 },
      {
        id: 112,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.bayLeaf'),
        quantity: 0.2,
        unit: 'g',
        price: 0.01,
      },
      { id: 113, category: 'dried_economat', name: t('onboarding.mockData.dishes.cassoulet.ingredients.thyme'), quantity: 1, unit: 'g', price: 0.05 },
      {
        id: 114,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.parsley'),
        quantity: 3,
        unit: 'g',
        price: 0.1,
      },
      {
        id: 115,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.clove'),
        quantity: 0.2,
        unit: 'g',
        price: 0.01,
      },
      {
        id: 116,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.pepper'),
        quantity: 1,
        unit: 'g',
        price: 0.02,
      },
      {
        id: 117,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.salt'),
        quantity: 2.5,
        unit: 'g',
        price: 0.01,
      },
      {
        id: 118,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.cassoulet.ingredients.duckFat'),
        quantity: 20,
        unit: 'g',
        price: 0.4,
      },
    ],
  };
}

/**
 * 2. Tarte Rustique (en tant qu'Entrée - ID -7)
 */
function createRusticTartDishStarter(): Dish {
  return {
    ...baseDish,
    id: -7,
    name: t('onboarding.mockData.dishes.rusticTart.name'),
    difficulty: 2,
    prep_time_min: 25,
    cook_time_min: 40,
    chill_time_min: 30,
    servings: 10,
    selected_image: 'none',
    allergens: `${t('onboarding.mockData.dishes.rusticTart.allergens.dairy')}, ${t('onboarding.mockData.dishes.rusticTart.allergens.egg')}, ${t('onboarding.mockData.dishes.rusticTart.allergens.gluten')}`,
    calories_kcal: 535,
    protein_g: 15.0,
    fat_g: 32.0,
    saturated_fat_g: 17.0,
    carbohydrates_g: 43.0,
    sugars_g: 5.0,
    dietary_fiber_g: 5.0,
    salt_sodium_g: 2.4,
    ingredients: [
      {
        id: 119,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.rusticTart.ingredients.leeks'),
        quantity: 80,
        unit: 'g',
        price: 0.64,
      },
      {
        id: 120,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.rusticTart.ingredients.potatoes'),
        quantity: 90,
        unit: 'g',
        price: 0.36,
      },
      { id: 121, category: 'meat', name: t('onboarding.mockData.dishes.rusticTart.ingredients.bacon'), quantity: 30, unit: 'g', price: 0.53 },
      { id: 122, category: 'dairy', name: t('onboarding.mockData.dishes.rusticTart.ingredients.cream'), quantity: 50, unit: 'ml', price: 0.45 },
      {
        id: 123,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.rusticTart.ingredients.flour'),
        quantity: 40,
        unit: 'g',
        price: 0.16,
      },
      { id: 124, category: 'dairy', name: t('onboarding.mockData.dishes.rusticTart.ingredients.butter'), quantity: 20, unit: 'g', price: 0.32 },
      { id: 125, category: 'dairy', name: t('onboarding.mockData.dishes.rusticTart.ingredients.cheese'), quantity: 20, unit: 'g', price: 0.6 },
      { id: 126, category: 'dried_economat', name: t('onboarding.mockData.dishes.rusticTart.ingredients.egg'), quantity: 30, unit: 'g', price: 0.4 },
      { id: 127, category: 'dried_economat', name: t('onboarding.mockData.dishes.rusticTart.ingredients.salt'), quantity: 2, unit: 'g', price: 0.01 },
      {
        id: 128,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.rusticTart.ingredients.pepper'),
        quantity: 0.5,
        unit: 'g',
        price: 0.02,
      },
      {
        id: 129,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.rusticTart.ingredients.garlic'),
        quantity: 1.5,
        unit: 'g',
        price: 0.03,
      },
      {
        id: 130,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.rusticTart.ingredients.onion'),
        quantity: 12,
        unit: 'g',
        price: 0.06,
      },
      {
        id: 131,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.rusticTart.ingredients.thyme'),
        quantity: 1,
        unit: 'g',
        price: 0.12,
      },
      {
        id: 132,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.rusticTart.ingredients.nutmeg'),
        quantity: 0.2,
        unit: 'g',
        price: 0.04,
      },
    ],
  };
}

/**
 * 3. Saint-Jacques (en tant qu'Entrée - ID -8)
 */
function createScallopsDishStarter(): Dish {
  return {
    ...baseDish,
    id: -8,
    name: t('onboarding.mockData.dishes.scallops.name'),
    difficulty: 3,
    prep_time_min: 30,
    cook_time_min: 35,
    chill_time_min: 0,
    servings: 10,
    selected_image: 'none',
    allergens: `${t('onboarding.mockData.dishes.scallops.allergens.dairy')}, ${t('onboarding.mockData.dishes.scallops.allergens.fish')}, ${t('onboarding.mockData.dishes.scallops.allergens.gluten')}`,
    calories_kcal: 530,
    protein_g: 25.0,
    fat_g: 18.0,
    saturated_fat_g: 8.0,
    carbohydrates_g: 57.0,
    sugars_g: 5.0,
    dietary_fiber_g: 4.0,
    salt_sodium_g: 1.6,
    ingredients: [
      { id: 133, category: 'fish', name: t('onboarding.mockData.dishes.scallops.ingredients.scallops'), quantity: 120, unit: 'g', price: 5.95 },
      {
        id: 134,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.scallops.ingredients.pumpkin'),
        quantity: 80,
        unit: 'g',
        price: 0.8,
      },
      {
        id: 135,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.scallops.ingredients.salsify'),
        quantity: 60,
        unit: 'g',
        price: 1.8,
      },
      { id: 136, category: 'dried_economat', name: t('onboarding.mockData.dishes.scallops.ingredients.rice'), quantity: 70, unit: 'g', price: 0.9 },
      { id: 137, category: 'dairy', name: t('onboarding.mockData.dishes.scallops.ingredients.butter'), quantity: 15, unit: 'g', price: 0.3 },
      { id: 138, category: 'dairy', name: t('onboarding.mockData.dishes.scallops.ingredients.parmesan'), quantity: 12, unit: 'g', price: 0.5 },
      { id: 139, category: 'dairy', name: t('onboarding.mockData.dishes.scallops.ingredients.cream'), quantity: 20, unit: 'ml', price: 0.2 },
      {
        id: 140,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.scallops.ingredients.shallots'),
        quantity: 10,
        unit: 'g',
        price: 0.2,
      },
      { id: 141, category: 'fruits_veggies', name: t('onboarding.mockData.dishes.scallops.ingredients.garlic'), quantity: 2, unit: 'g', price: 0.05 },
      { id: 142, category: 'fruits_veggies', name: t('onboarding.mockData.dishes.scallops.ingredients.parsley'), quantity: 2, unit: 'g', price: 0.1 },
      {
        id: 143,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.scallops.ingredients.oliveOil'),
        quantity: 20,
        unit: 'ml',
        price: 0.4,
      },
      {
        id: 144,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.scallops.ingredients.vegBroth'),
        quantity: 200,
        unit: 'ml',
        price: 0.3,
      },
      { id: 145, category: 'dried_economat', name: t('onboarding.mockData.dishes.scallops.ingredients.salt'), quantity: 3, unit: 'g', price: 0.02 },
      { id: 146, category: 'dried_economat', name: t('onboarding.mockData.dishes.scallops.ingredients.pepper'), quantity: 1, unit: 'g', price: 0.03 },
      {
        id: 147,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.scallops.ingredients.nutmeg'),
        quantity: 0.3,
        unit: 'g',
        price: 0.04,
      },
      { id: 148, category: 'alcohol', name: t('onboarding.mockData.dishes.scallops.ingredients.whiteWine'), quantity: 30, unit: 'ml', price: 0.3 },
      {
        id: 149,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.scallops.ingredients.sunflowerOil'),
        quantity: 20,
        unit: 'ml',
        price: 0.15,
      },
    ],
  };
}

/**
 * 4. Lasagnes Butternut (en tant qu'Entrée - ID -9)
 */
function createButternutLasagnaDishStarter(): Dish {
  return {
    ...baseDish,
    id: -9,
    name: t('onboarding.mockData.dishes.butternutLasagna.name'),
    difficulty: 3,
    prep_time_min: 30,
    cook_time_min: 45,
    servings: 10,
    selected_image: 'none',
    allergens: `${t('onboarding.mockData.dishes.butternutLasagna.allergens.dairy')}, ${t('onboarding.mockData.dishes.butternutLasagna.allergens.gluten')}`,
    calories_kcal: 480,
    protein_g: 20.0,
    fat_g: 21.0,
    saturated_fat_g: 12.0,
    carbohydrates_g: 47.0,
    sugars_g: 10.0,
    dietary_fiber_g: 6.0,
    salt_sodium_g: 1.6,
    ingredients: [
      {
        id: 150,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.butternut'),
        quantity: 200,
        unit: 'g',
        price: 0.8,
      },
      {
        id: 151,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.spinach'),
        quantity: 100,
        unit: 'g',
        price: 0.8,
      },
      {
        id: 152,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.ricotta'),
        quantity: 120,
        unit: 'g',
        price: 1.7,
      },
      {
        id: 153,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.parmesan'),
        quantity: 20,
        unit: 'g',
        price: 0.5,
      },
      {
        id: 154,
        category: 'dairy',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.mozzarella'),
        quantity: 30,
        unit: 'g',
        price: 0.42,
      },
      {
        id: 155,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.pasta'),
        quantity: 40,
        unit: 'g',
        price: 0.3,
      },
      { id: 156, category: 'dairy', name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.milk'), quantity: 120, unit: 'ml', price: 0.22 },
      { id: 157, category: 'dairy', name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.butter'), quantity: 12, unit: 'g', price: 0.23 },
      {
        id: 158,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.flour'),
        quantity: 10,
        unit: 'g',
        price: 0.04,
      },
      {
        id: 159,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.onion'),
        quantity: 20,
        unit: 'g',
        price: 0.08,
      },
      {
        id: 160,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.garlic'),
        quantity: 3,
        unit: 'g',
        price: 0.03,
      },
      {
        id: 161,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.nutmeg'),
        quantity: 0.5,
        unit: 'g',
        price: 0.04,
      },
      {
        id: 162,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.sage'),
        quantity: 1,
        unit: 'g',
        price: 0.07,
      },
      {
        id: 163,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.thyme'),
        quantity: 1,
        unit: 'g',
        price: 0.07,
      },
      {
        id: 164,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.salt'),
        quantity: 2.5,
        unit: 'g',
        price: 0.01,
      },
      {
        id: 165,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.pepper'),
        quantity: 1,
        unit: 'g',
        price: 0.04,
      },
      {
        id: 166,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.butternutLasagna.ingredients.oliveOil'),
        quantity: 20,
        unit: 'ml',
        price: 0.2,
      },
    ],
  };
}

/**
 * 5. Gratin Ravioles (en tant qu'Entrée - ID -10)
 */
function createRaviolesGratinDishStarter(): Dish {
  return {
    ...baseDish,
    id: -10,
    name: t('onboarding.mockData.dishes.raviolesGratin.name'),
    difficulty: 3,
    prep_time_min: 20,
    cook_time_min: 30,
    servings: 10,
    selected_image: 'none',
    allergens: `${t('onboarding.mockData.dishes.raviolesGratin.allergens.dairy')}, ${t('onboarding.mockData.dishes.raviolesGratin.allergens.nuts')}, ${t('onboarding.mockData.dishes.raviolesGratin.allergens.gluten')}`,
    calories_kcal: 530,
    protein_g: 20.0,
    fat_g: 32.0,
    saturated_fat_g: 16.0,
    carbohydrates_g: 40.0,
    sugars_g: 4.0,
    dietary_fiber_g: 5.0,
    salt_sodium_g: 1.8,
    ingredients: [
      {
        id: 167,
        category: 'frozen',
        name: t('onboarding.mockData.dishes.raviolesGratin.ingredients.ravioles'),
        quantity: 120,
        unit: 'g',
        price: 2.2,
      },
      {
        id: 168,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.raviolesGratin.ingredients.spinach'),
        quantity: 80,
        unit: 'g',
        price: 0.8,
      },
      { id: 169, category: 'dairy', name: t('onboarding.mockData.dishes.raviolesGratin.ingredients.beaufort'), quantity: 40, unit: 'g', price: 1.8 },
      { id: 170, category: 'dairy', name: t('onboarding.mockData.dishes.raviolesGratin.ingredients.cream'), quantity: 80, unit: 'ml', price: 0.72 },
      { id: 171, category: 'dairy', name: t('onboarding.mockData.dishes.raviolesGratin.ingredients.milk'), quantity: 60, unit: 'ml', price: 0.24 },
      {
        id: 172,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.raviolesGratin.ingredients.hazelnuts'),
        quantity: 12,
        unit: 'g',
        price: 0.7,
      },
      {
        id: 173,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.raviolesGratin.ingredients.oliveOil'),
        quantity: 4,
        unit: 'ml',
        price: 0.08,
      },
      {
        id: 174,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.raviolesGratin.ingredients.garlic'),
        quantity: 1.5,
        unit: 'g',
        price: 0.05,
      },
      {
        id: 175,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.raviolesGratin.ingredients.shallot'),
        quantity: 5,
        unit: 'g',
        price: 0.11,
      },
      {
        id: 176,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.raviolesGratin.ingredients.salt'),
        quantity: 1.5,
        unit: 'g',
        price: 0.01,
      },
      {
        id: 177,
        category: 'dried_economat',
        name: t('onboarding.mockData.dishes.raviolesGratin.ingredients.pepper'),
        quantity: 0.5,
        unit: 'g',
        price: 0.04,
      },
      {
        id: 178,
        category: 'fruits_veggies',
        name: t('onboarding.mockData.dishes.raviolesGratin.ingredients.nutmeg'),
        quantity: 0.2,
        unit: 'g',
        price: 0.04,
      },
    ],
  };
}

// Map des factory functions pour les entrées
export const starterFactories: Record<number, () => Dish> = {
  [-6]: createCassouletDishStarter,
  [-7]: createRusticTartDishStarter,
  [-8]: createScallopsDishStarter,
  [-9]: createButternutLasagnaDishStarter,
  [-10]: createRaviolesGratinDishStarter,
};
